/**
 * greenbook "soft cards" kit (docs/markets-ux.md §0) — the shared look every screen moves to,
 * starting with Markets. Taken from the trade ticket: rounded-lg cards on panel-2 with a subtle
 * border, rounded-full pills with an amber active state, roomy padding, 13–14px body text,
 * prices in `nums`, quick motion that's off under prefers-reduced-motion.
 *
 *   SoftCard            card surface; `href` makes the whole card a link (stretched-link pattern,
 *                       so buttons inside stay real buttons and the card still middle-clicks)
 *   Pill                rounded-full chip / filter pill; amber when active
 *   SectionHeading      full-width group header (spans the whole grid row)
 *   PriceButton         a price tap target: label, price in the user's odds format, best venue;
 *                       flashes green/red ~600ms when the price moves
 *   PriceButtonSkeleton the same footprint while prices load
 *   FlashText           any number with the PriceButton's green/red flash when it moves
 *   BottomSheet         the mobile sheet (backdrop, ✕/Escape/tap-outside, body scroll lock)
 *   TicketDrawer        the trade-ticket drawer: a right-side push drawer on desktop (Escape
 *                       closes), the BottomSheet on mobile (Markets, Portfolio, the game page)
 *   Skeleton            re-exported from ui.tsx
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { fmtPrice } from "../lib/format.js";
import { useOddsFormat } from "../lib/odds.js";
import { Skeleton } from "./ui.js";

export { Skeleton };

/* ------------------------------------------------------------------- SoftCard */

export function SoftCard({
  children,
  href,
  linkLabel,
  dim = false,
  highlight = false,
  className = "",
}: {
  children: ReactNode;
  /** Makes the whole card a link. Interactive children must sit above it (`relative z-10`). */
  href?: string;
  /** Accessible name for the card link. */
  linkLabel?: string;
  /** Quieter surface (e.g. no tradeable prices). */
  dim?: boolean;
  /** Accent ring (e.g. the Live now section). */
  highlight?: boolean;
  className?: string;
}) {
  return (
    <div
      className={`group relative rounded-lg border bg-panel-2 transition-colors duration-150 motion-reduce:transition-none ${
        highlight ? "border-blue/30" : "border-border"
      } ${href ? "hover:border-border-2" : ""} ${dim ? "opacity-60 hover:opacity-90" : ""} ${className}`}
    >
      {href && (
        <Link
          to={href}
          aria-label={linkLabel}
          className="absolute inset-0 z-0 rounded-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-amber/70"
        />
      )}
      {children}
    </div>
  );
}

/* ----------------------------------------------------------------------- Pill */

export function Pill({
  children,
  active = false,
  onClick,
  count,
  icon,
  title,
  className = "",
  "aria-label": ariaLabel,
}: {
  children: ReactNode;
  active?: boolean;
  onClick?: () => void;
  /** Trailing count; hidden when null/0. */
  count?: number | null;
  icon?: ReactNode;
  title?: string;
  className?: string;
  "aria-label"?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={ariaLabel}
      aria-pressed={active}
      className={`flex min-h-11 shrink-0 items-center gap-1.5 rounded-full border px-3.5 text-[13px] font-medium transition-colors duration-150 motion-reduce:transition-none lg:min-h-8 lg:px-3 ${
        active
          ? "border-amber/60 bg-amber/15 text-amber"
          : "border-border-2 bg-panel-2 text-dim hover:border-border-2 hover:bg-raised hover:text-ink"
      } ${className}`}
    >
      {icon}
      <span className="whitespace-nowrap">{children}</span>
      {count != null && count > 0 && (
        <span className={`nums text-[11px] ${active ? "text-amber/80" : "text-faint"}`}>{count}</span>
      )}
    </button>
  );
}

/* ------------------------------------------------------------- SectionHeading */

export function SectionHeading({
  title,
  count,
  tone = "default",
  right,
  className = "",
}: {
  title: ReactNode;
  count?: number;
  /** `live` adds the pulsing blue dot. */
  tone?: "default" | "live";
  right?: ReactNode;
  className?: string;
}) {
  return (
    <div className={`col-span-full flex items-center gap-2 pb-0.5 pt-3 first:pt-0 ${className}`}>
      {tone === "live" && <span className="pulse inline-block h-2 w-2 rounded-full bg-blue" aria-hidden />}
      <h2 className={`text-[11px] font-semibold uppercase tracking-wider ${tone === "live" ? "text-blue" : "text-dim"}`}>
        {title}
      </h2>
      {count != null && <span className="nums text-[11px] text-faint">{count}</span>}
      {right && <div className="ml-auto flex items-center gap-2">{right}</div>}
    </div>
  );
}

/* ---------------------------------------------------------------- PriceButton */

/** Remember the last value and report the direction of each change (null on first render). */
export function useFlash(price: number | null | undefined): { dir: "up" | "down"; n: number } | null {
  const prev = useRef(price);
  const [flash, setFlash] = useState<{ dir: "up" | "down"; n: number } | null>(null);
  useEffect(() => {
    const before = prev.current;
    prev.current = price;
    if (typeof before === "number" && typeof price === "number" && before !== price) {
      setFlash((f) => ({ dir: price > before ? "up" : "down", n: (f?.n ?? 0) + 1 }));
    }
  }, [price]);
  return flash;
}

export function PriceButton({
  label,
  price,
  american,
  venue,
  onClick,
  active = false,
  disabled = false,
  layout = "tile",
  emptyText = "No price",
  title,
  className = "",
}: {
  /** What you're buying (team / outcome). Omit it when a row/column header already says so. */
  label?: ReactNode;
  price: number | null | undefined;
  american?: string | null;
  /** Best venue name. */
  venue?: string | null;
  onClick?: () => void;
  /** Highlighted (its ticket is open). */
  active?: boolean;
  disabled?: boolean;
  /** `tile`: label over price (moneyline sides). `row`: label left, price right (futures). */
  layout?: "tile" | "row";
  /** Shown in place of the price when there is none. */
  emptyText?: string;
  title?: string;
  /** Extra classes on the button (e.g. `h-full`, room for an inline "+N"). */
  className?: string;
}) {
  const { format } = useOddsFormat();
  const flash = useFlash(price);
  const has = typeof price === "number";
  const priceText = has ? fmtPrice(price, american, format) : null;

  const tone = disabled
    ? "cursor-not-allowed border-border/60 bg-transparent"
    : active
      ? "border-amber/70 bg-amber/10"
      : "border-border-2 bg-panel hover:border-amber/40 hover:bg-raised";

  const priceEl = has ? (
    <span className="flex min-w-0 items-baseline gap-1.5">
      <span className={`nums shrink-0 font-semibold ${active ? "text-amber" : "text-ink"} ${layout === "tile" ? "text-[15px]" : "text-[14px]"}`}>
        {priceText}
      </span>
      {venue && <span className="min-w-0 truncate text-[11px] text-dim">· {venue}</span>}
    </span>
  ) : (
    <span className="text-[12px] text-faint">{emptyText}</span>
  );

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-pressed={active}
      className={`relative z-10 flex min-h-11 w-full min-w-0 overflow-hidden rounded-md border text-left transition-colors duration-150 motion-reduce:transition-none ${tone} ${
        layout === "tile" ? "flex-col justify-center gap-0.5 px-3 py-2" : "items-center gap-3 px-3 py-1.5"
      } ${className}`}
    >
      {flash && (
        <span
          key={flash.n}
          aria-hidden
          className={`pointer-events-none absolute inset-0 ${flash.dir === "up" ? "gb-flash-up" : "gb-flash-down"}`}
        />
      )}
      {label != null && label !== "" && (
        <span
          className={`relative min-w-0 text-[13px] ${disabled ? "text-faint" : "text-ink"} ${
            layout === "tile" ? "truncate" : "flex-1 truncate"
          }`}
        >
          {label}
        </span>
      )}
      <span className={`relative ${layout === "row" ? "max-w-[55%] shrink-0" : ""}`}>{priceEl}</span>
    </button>
  );
}

/**
 * Any number that should flash like a PriceButton when it moves (Portfolio's Value now and each
 * row's "price now"): the same green-up / red-down tint over ~600 ms, off under reduced motion.
 * `value` drives the direction; `children` is what's shown.
 */
export function FlashText({
  value,
  children,
  className = "",
}: {
  value: number | null | undefined;
  children: ReactNode;
  className?: string;
}) {
  const flash = useFlash(value);
  return (
    <span className={`relative -mx-1 inline-flex items-baseline rounded px-1 ${className}`}>
      {flash && (
        <span
          key={flash.n}
          aria-hidden
          className={`pointer-events-none absolute inset-0 rounded ${flash.dir === "up" ? "gb-flash-up" : "gb-flash-down"}`}
        />
      )}
      <span className="relative">{children}</span>
    </span>
  );
}

export function PriceButtonSkeleton({ layout = "tile" }: { layout?: "tile" | "row" }) {
  return (
    <div
      aria-hidden
      className={`flex min-h-11 w-full rounded-md border border-border/60 ${
        layout === "tile" ? "flex-col justify-center gap-1.5 px-3 py-2" : "items-center justify-between gap-3 px-3 py-1.5"
      }`}
    >
      <Skeleton className="h-3 w-20 max-w-[60%]" />
      <Skeleton className={layout === "tile" ? "h-3.5 w-24 max-w-[70%]" : "h-3.5 w-16"} />
    </div>
  );
}

/* ---------------------------------------------------------------- BottomSheet */

/**
 * The mobile bottom sheet (same motion as the game page's): slides up, backdrop fades in, closes
 * on the ✕, Escape, or a tap on the backdrop. Locks body scroll while open.
 */
export function BottomSheet({
  open,
  onClose,
  label,
  header,
  children,
}: {
  open: boolean;
  onClose: () => void;
  label: string;
  /** Optional header content left of the ✕. */
  header?: ReactNode;
  children: ReactNode;
}) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label={label}>
      <div
        className="absolute inset-0 bg-black/50 animate-[gb-fade-in_0.2s_ease-out] motion-reduce:animate-none"
        onClick={onClose}
        aria-hidden
      />
      <div className="absolute inset-x-0 bottom-0 flex max-h-[88dvh] transform-gpu flex-col overflow-hidden rounded-t-xl border-t border-border bg-panel shadow-2xl will-change-transform animate-[gb-sheet-up_0.28s_cubic-bezier(0.32,0.72,0,1)] motion-reduce:animate-none">
        <div className="shrink-0 border-b border-border bg-panel-2 px-2 pb-1.5 pt-2">
          <div className="mx-auto mb-1.5 h-1 w-9 rounded-full bg-border-2" aria-hidden />
          <div className="flex items-center gap-1">
            <div className="min-w-0 flex-1 px-2">{header}</div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="grid h-11 w-11 shrink-0 place-items-center rounded-md text-dim hover:bg-raised hover:text-ink"
            >
              ✕
            </button>
          </div>
        </div>
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">{children}</div>
      </div>
    </div>
  );
}

/* --------------------------------------------------------------- TicketDrawer */

/**
 * The trade-ticket drawer shared by Markets, Portfolio and the game page. Desktop: a 380px
 * right-side drawer that PUSHES the content (render it as the last child of a flex row, beside
 * the scrolling content) and closes on Escape. Mobile: the shared BottomSheet. `children` is
 * the ticket (and anything that goes with it); it's only rendered while open.
 */
export function TicketDrawer({
  open,
  onClose,
  mobile,
  header,
  label = "Trade ticket",
  children,
}: {
  open: boolean;
  onClose: () => void;
  /** Render as the mobile bottom sheet instead of the desktop drawer. */
  mobile: boolean;
  /** The sheet's header content (mobile only). */
  header?: ReactNode;
  label?: string;
  children: ReactNode;
}) {
  // Escape closes the desktop drawer (the mobile sheet handles its own).
  useEffect(() => {
    if (!open || mobile) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !e.defaultPrevented) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, mobile, onClose]);

  if (mobile) {
    return (
      <BottomSheet open={open} onClose={onClose} label={label} header={header}>
        {open && children}
      </BottomSheet>
    );
  }
  if (!open) return null;
  return (
    <aside className="gb-drawer-in flex w-[380px] shrink-0 flex-col border-l border-border bg-panel" aria-label={label}>
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">{children}</div>
    </aside>
  );
}
